import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import LinearProgress from '@mui/material/LinearProgress';
import Upload from '@mui/icons-material/Upload';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { visuallyHidden } from '@mui/utils';
import { FileDropZone } from '../components/upload/FileDropZone';
import { FileList } from '../components/upload/FileList';
import { UploadService } from '../services/uploadService';
import { useUploadContext } from '../contexts/UploadContext';
import { FileWithMetadata, QuotaStatus } from '../types/Upload';
import { demoAuthService } from '../services/demoAuthService';
import { authService } from '../services/authService';
import { getUnsupportedPlayableExtensions } from '../utils/audioUtils';
import { DemoContactRedirect } from '../components/demo/DemoContactRedirect';
import { CallService } from '../services/callService';
import { notificationService } from '../services/notificationService';
import { validatePdf } from '../components/calldetails/cadRecordDisplay';
import { isDemoMode } from '../config';

interface UploadFormData {
  incidentNumber: string;
  callType: string;
  callDate: string;
}

const isPdf = (file: File) => file.name.toLowerCase().endsWith('.pdf');

export const UploadPage: React.FC = () => {
  const navigate = useNavigate();

  // ALL HOOKS MUST BE AT THE TOP - BEFORE ANY CONDITIONAL LOGIC
  // Staged (not yet dispatched) files are page-local; everything after the Upload
  // click is owned by UploadContext and survives navigating away from this page.
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [formData] = useState<UploadFormData>({
    incidentNumber: '',
    callType: '911',
    callDate: '', // Will use upload timestamp
  });
  const { items: uploadItems, isUploading, dispatch } = useUploadContext();
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [roleChecked, setRoleChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const wasUploadingRef = useRef(false);
  // A batch that includes CAD reports ends on the dashboard, where the CAD column and the "waiting for
  // a call" notice show the result. Set when such a batch is started; the effect below acts on it once
  // the audio and the reports have all finished, and only if none failed.
  const redirectWhenDoneRef = useRef(false);
  // CAD event report PDFs travel with the audio when the agency has switched that on. They go to a
  // shelf of their own and are matched to calls by the server, so the audio pipeline is untouched.
  const [acceptCadPdf, setAcceptCadPdf] = useState(false);
  const [cadUploads, setCadUploads] = useState<FileWithMetadata[]>([]);

  // Fetch quota status on mount and after successful uploads
  const fetchQuotaStatus = useCallback(async () => {
    try {
      setQuotaLoading(true);
      const status = await UploadService.getQuotaStatus();
      setQuotaStatus(status);
    } catch (error) {
      console.error('Failed to fetch quota status:', error);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotaStatus();
  }, [fetchQuotaStatus]);

  useEffect(() => {
    CallService.getCadInboxSummary()
      .then((summary) => setAcceptCadPdf(!!summary?.bulkUploadEnabled))
      .catch(() => setAcceptCadPdf(false));
  }, []);

  const handleFilesSelected = useCallback((selectedFiles: File[]) => {
    // Validate files: PDFs as CAD reports when the agency accepts them, everything else as audio.
    const pdfs = acceptCadPdf ? selectedFiles.filter(isPdf) : [];
    const audio = acceptCadPdf ? selectedFiles.filter((f) => !isPdf(f)) : selectedFiles;
    const validationErrors = [
      ...(audio.length > 0 ? UploadService.validateUploadRequest(audio) : []),
      ...pdfs.flatMap((f) => {
        const problem = validatePdf(f);
        return problem ? [`${f.name}: ${problem}`] : [];
      }),
    ];
    if (validationErrors.length > 0) {
      setUploadErrors(validationErrors);
      return;
    }

    // Clear any previous errors
    setUploadErrors([]);

    // Convert to FileWithMetadata
    const newFiles: FileWithMetadata[] = selectedFiles.map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      progress: 0,
      status: 'pending',
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  }, [acceptCadPdf]);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.id !== fileId));
  }, []);

  // Sequential: a CAD report is small, and one at a time keeps the server's shelf-full answer
  // attached to the file that met it. This page owns the list, so it is visible only here.
  const uploadCadReports = useCallback(async (pdfs: File[]) => {
    const rows: FileWithMetadata[] = pdfs.map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      progress: 0,
      status: 'uploading',
    }));
    setCadUploads(rows);

    let failed = 0;
    for (const row of rows) {
      try {
        await CallService.uploadCadInboxFile(row.file);
        setCadUploads((prev) => prev.map((r) => (r.id === row.id ? { ...r, progress: 100, status: 'completed' } : r)));
      } catch (error) {
        failed++;
        const message =
          (error as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'The CAD report could not be uploaded.';
        setCadUploads((prev) =>
          prev.map((r) => (r.id === row.id ? { ...r, status: 'error', errorMessage: message } : r))
        );
      }
    }

    const done = rows.length - failed;

    if (done > 0) {
      setSuccessMessage(
        `${done} CAD report${done > 1 ? 's' : ''} uploaded. They are matched to calls within a few minutes. Calls still missing one are marked on the dashboard.`
      );
    }
    if (failed > 0) {
      setUploadErrors([`${failed} CAD report${failed > 1 ? 's' : ''} could not be uploaded. See the list below.`]);
    }
  }, []);

  const handleUpload = useCallback(() => {
    if (files.length === 0) {
      setUploadErrors(['No files selected']);
      return;
    }

    if (!formData.callType) {
      setUploadErrors(['Call type is required']);
      return;
    }

    setUploadErrors([]);

    const audioFiles = files.filter((f) => !isPdf(f.file));
    const reportFiles = files.filter((f) => isPdf(f.file));

    // Hand the audio queue to the app-level UploadContext and clear the staging list.
    // From here the upload is fire-and-forget: it survives navigating anywhere,
    // and the toolbar chip tracks progress and failures.
    if (audioFiles.length > 0) {
      dispatch(
        audioFiles.map((f) => f.file),
        {
          incidentNumber: formData.incidentNumber || undefined,
          callType: formData.callType,
          callDate: formData.callDate || undefined,
        }
      );
    }
    redirectWhenDoneRef.current = reportFiles.length > 0;
    if (reportFiles.length > 0) void uploadCadReports(reportFiles.map((f) => f.file));

    setFiles([]);
    if (audioFiles.length > 0) {
      setSuccessMessage(`Upload of ${audioFiles.length} file(s) started — you can navigate away.`);
    }
  }, [files, formData, dispatch, uploadCadReports]);

  // Move on to the dashboard when a batch that included CAD reports has completely finished. Waits for
  // the audio (which uploads in the background) as well as the reports; a failure of either keeps the
  // person here to read it. Navigation is not state, so this is not a state-setting effect.
  useEffect(() => {
    if (!redirectWhenDoneRef.current) return;

    const reportsBusy = cadUploads.some((r) => r.status === 'pending' || r.status === 'uploading');
    if (reportsBusy || isUploading) return;

    redirectWhenDoneRef.current = false;
    const anyFailed = cadUploads.some((r) => r.status === 'error') || uploadItems.some((i) => i.status === 'error');
    if (anyFailed) return;

    const reports = cadUploads.length;
    notificationService.success(
      'CAD reports uploaded',
      `${reports} CAD report${reports === 1 ? '' : 's'} sent. They are matched to calls within a few minutes. Calls still missing one are marked on the dashboard.`
    );
    navigate('/');
  }, [cadUploads, isUploading, uploadItems, navigate]);

  // Refresh quota when the active upload session drains.
  useEffect(() => {
    if (wasUploadingRef.current && !isUploading) {
      fetchQuotaStatus();
    }
    wasUploadingRef.current = isUploading;
  }, [isUploading, fetchQuotaStatus]);

  // Check demo user permissions
  useEffect(() => {
    if (isDemoMode && !demoAuthService.canUploadFiles()) {
      // Redirect to demo contact page
      navigate('/demo/upload', { replace: true });
      return;
    }

    // Production: check for TenantAdmin or Supervisor role from DB
    if (!isDemoMode) {
      authService.getUserAsync().then(user => {
        if (!user || (user.role !== 'TenantAdmin' && user.role !== 'Supervisor')) {
          navigate('/', { replace: true });
        } else {
          setHasAccess(true);
        }
        setRoleChecked(true);
      });
      return;
    }
    setRoleChecked(true);
    setHasAccess(true);
  }, [navigate]);

  // If demo user without upload permissions, show contact redirect
  if (isDemoMode && !demoAuthService.canUploadFiles()) {
    return <DemoContactRedirect feature="upload" />;
  }

  // If production user without proper role, don't render
  if (!isDemoMode && (!roleChecked || !hasAccess)) {
    return null;
  }

  const canUpload = files.length > 0 && Boolean(formData.callType);
  const pendingFileCount = files.filter(f => !isPdf(f.file) && (f.status === 'pending' || f.status === 'error')).length;
  const exceedsQuota = quotaStatus != null && quotaStatus.remaining > 0 && pendingFileCount > quotaStatus.remaining;

  // Files already handed to the UploadContext, rendered read-only below the staging list.
  // The pipeline's presigning/confirming sub-states collapse to "uploading" for display;
  // "verifying" (session-end reconcile) is surfaced so a lost-response file isn't shown as failed.
  const inFlightFiles: FileWithMetadata[] = uploadItems.map((item) => ({
    file: item.file,
    id: item.id,
    progress: item.progress,
    status:
      item.status === 'completed' || item.status === 'error' ||
      item.status === 'pending' || item.status === 'verifying'
        ? item.status
        : 'uploading',
    errorMessage: item.errorMessage,
  }));
  const { uploadDone, uploadFailed } = inFlightFiles.reduce(
    (acc, f) => {
      if (f.status === 'completed') acc.uploadDone++;
      else if (f.status === 'error') acc.uploadFailed++;
      return acc;
    },
    { uploadDone: 0, uploadFailed: 0 }
  );
  const uploadActive = inFlightFiles.length - uploadDone - uploadFailed;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Upload Audio Files
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Upload audio files for AI-powered analysis and quality assurance scoring.
      </Typography>

      {/* Quota Status Display */}
      {quotaLoading ? (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      ) : quotaStatus ? (
        <Alert 
          severity={quotaStatus.remaining === 0 ? 'error' : quotaStatus.remaining < 10 ? 'warning' : 'info'}
          icon={<InfoOutlined />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2">
            <strong>Daily Upload Quota:</strong> {quotaStatus.used} of {quotaStatus.limit} files used today
            {quotaStatus.remaining > 0 ? (
              <> ({quotaStatus.remaining} remaining)</>
            ) : (
              <> - Limit reached. Resets at {new Date(quotaStatus.resetsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.</>
            )}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={(quotaStatus.used / quotaStatus.limit) * 100}
            sx={{ mt: 1, height: 8, borderRadius: 1 }}
            color={quotaStatus.remaining === 0 ? 'error' : quotaStatus.remaining < 10 ? 'warning' : 'primary'}
          />
        </Alert>
      ) : null}

      <FileDropZone onFilesSelected={handleFilesSelected} disabled={quotaStatus?.remaining === 0} quotaStatus={quotaStatus} acceptCadPdf={acceptCadPdf} />

      {/* WMA playback warning — shown only when .wma files are actually queued */}
      {files.some(f => getUnsupportedPlayableExtensions().some(ext => f.file.name.toLowerCase().endsWith(ext))) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> .wma files can be uploaded but may not play in your browser. Consider converting to MP3, WAV, or M4A for better compatibility.
          </Typography>
        </Alert>
      )}

      {/* Over-quota warning — delete stays enabled so user can trim the list */}
      {exceedsQuota && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Too many files selected.</strong> You have {pendingFileCount} files queued but only {quotaStatus!.remaining} upload{quotaStatus!.remaining !== 1 ? 's' : ''} remaining today. Please remove at least {pendingFileCount - quotaStatus!.remaining} file{pendingFileCount - quotaStatus!.remaining !== 1 ? 's' : ''} before uploading, or clear all and select fewer files.
          </Typography>
        </Alert>
      )}

      <FileList files={files} onRemoveFile={handleRemoveFile} />

      {files.length > 0 && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleUpload}
            disabled={!canUpload || exceedsQuota || (quotaStatus?.remaining === 0)}
            size="large"
          >
            {`Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
          </Button>

          <Button variant="outlined" onClick={() => setFiles([])}>
            Clear All
          </Button>
        </Box>
      )}

      {/* Files handed off to the app-level upload queue — progress survives navigation */}
      {inFlightFiles.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Upload Progress
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These files upload in the background. You can leave this page — progress stays
            in the toolbar indicator. Closing the browser tab cancels unfinished files.
          </Typography>
          <FileList files={inFlightFiles} disabled />
          {isUploading && (
            <Box aria-live="polite" sx={visuallyHidden}>
              {`Uploading: ${uploadActive} in progress, ${uploadDone} completed${uploadFailed ? `, ${uploadFailed} failed` : ''}.`}
            </Box>
          )}
        </Box>
      )}

      {/* CAD reports sent to the shelf: their own list, because they are matched, not scored */}
      {cadUploads.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            CAD Reports
          </Typography>
          <FileList files={cadUploads} disabled />
        </Box>
      )}

      {/* Error Messages */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Errors:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {uploadErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
